import type { GiftPassBundle } from '@bonsai/shared';

import { DashboardsPage } from '../../dashboards/DashboardsPage';
import type { EditorChromeProps } from '../../shared/editor-page/editor-chrome';
import { EditorHeader } from '../../shared/editor-page/EditorHeader';
import { EditorSaveProblem } from '../../shared/editor-page/EditorSaveProblem';
import { GIFT_PASS_BUNDLES_LIST_PATH } from '../gift-pass-bundles-paths';
import { useAllGiftPassBundles } from '../use-gift-pass-bundles';

import type { BundleFormValues } from './bundle-form';
import { BundleCostPreview } from './BundleCostPreview';
import { BundleCustomerPreview } from './BundleCustomerPreview';
import { BundleFormCard } from './BundleFormCard';
import { BundleStatusCard } from './BundleStatusCard';
import { useBundleCostPreview } from './use-bundle-cost-preview';
import { useBundleEditor } from './use-bundle-editor';

import '../../shared/editor-page/editor-page.css';

export interface GiftPassBundleEditorProps {
  /** The saved bundle being edited; absent for a new one. */
  bundle?: GiftPassBundle;
  initial: BundleFormValues;
  initiallySaved?: boolean;
  persist: (values: BundleFormValues, base: BundleFormValues) => Promise<GiftPassBundle>;
  onSaved?: (bundle: GiftPassBundle) => void;
}

export function GiftPassBundleEditor(p: GiftPassBundleEditorProps) {
  const editor = useBundleEditor(p);
  const cost = useBundleCostPreview(editor.values);
  const all = useAllGiftPassBundles();
  const isNew = !p.bundle;
  const savedName = p.bundle?.name ?? '';
  const promotedElsewhere =
    all.data?.bundles.find((b) => b.isPromoted && b.id !== p.bundle?.id)?.name ?? null;

  const chrome: EditorChromeProps = {
    title: isNew ? 'New bundle' : savedName,
    section: 'Marketing',
    listLabel: 'Gift pass bundles',
    crumb: isNew ? 'New' : savedName,
    noun: 'bundle',
    listPath: GIFT_PASS_BUNDLES_LIST_PATH,
    isNew,
    dirty: editor.dirty,
    saved: editor.saved,
    saving: editor.saving,
    onSave: () => void editor.submit(),
  };

  return (
    <DashboardsPage header={<EditorHeader {...chrome} />}>
      {editor.errors.form && <EditorSaveProblem message={editor.errors.form} />}
      <div className="nb-ed-grid">
        <div className="nb-ed-form">
          <BundleFormCard editor={editor} atFallback={cost.atFallback} />
        </div>
        <div className="nb-ed-status">
          <BundleStatusCard
            values={editor.values}
            errors={editor.errors}
            isNew={isNew}
            savedPromoted={editor.base.isPromoted}
            promotedElsewhere={promotedElsewhere}
            onChange={editor.set}
          />
        </div>
        <div className="nb-ed-preview">
          <BundleCustomerPreview values={editor.values} atFallback={cost.atFallback} />
        </div>
        <div className="nb-ed-extra">
          <BundleCostPreview
            ready={cost.ready}
            preview={cost.preview}
            loading={!cost.upToDate}
            failure={cost.failure}
          />
        </div>
      </div>
    </DashboardsPage>
  );
}
